import { useCallback, useEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useLanguage } from '../../i18n';
import { usePrefersReducedMotion } from '../../hooks/useMediaQuery';
import { hgroupNegro } from '../../assets/images/logo';
import './Footer.scss';

gsap.registerPlugin(ScrollTrigger);

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const WHATSAPP_NUMBER = import.meta.env.VITE_WHATSAPP_NUMBER ?? '525619933412';
const GOOGLE_MAPS_URL = import.meta.env.VITE_GOOGLE_MAPS_URL ?? '';
const MAP_EMBED_URL =
  'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3762.1397!2d-99.2086!3d19.4492!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x85d20200b0000000%3A0x0!2sBlvd.%20Palmas%20Hills%201%2C%20Piso%2021!5e0!3m2!1ses!2smx!4v1647891234567!5m2!1ses!2smx';

const EMPTY_FORM = { name: '', email: '', phone: '', message: '' };

const Footer = () => {
  const { t } = useLanguage();
  const reduceMotion = usePrefersReducedMotion();
  const footerRef = useRef(null);
  const formRef = useRef(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (reduceMotion) return undefined;

    const ctx = gsap.context(() => {
      gsap.fromTo(
        '.footer__title',
        { opacity: 0, y: 30 },
        {
          opacity: 1,
          y: 0,
          duration: 1,
          scrollTrigger: { trigger: '.footer__title', start: 'top 85%' },
        },
      );

      gsap.fromTo(
        '.footer__subtitle',
        { opacity: 0, y: 30 },
        {
          opacity: 1,
          y: 0,
          duration: 1,
          delay: 0.1,
          scrollTrigger: { trigger: '.footer__subtitle', start: 'top 85%' },
        },
      );

      gsap.fromTo(
        '.footer__form-group',
        { opacity: 0, y: 20 },
        {
          opacity: 1,
          y: 0,
          duration: 0.8,
          stagger: 0.1,
          scrollTrigger: { trigger: '.footer__form', start: 'top 85%' },
        },
      );

      gsap.fromTo(
        '.footer__map-container',
        { opacity: 0, scale: 0.95 },
        {
          opacity: 1,
          scale: 1,
          duration: 1,
          scrollTrigger: { trigger: '.footer__map-container', start: 'top 85%' },
        },
      );
    }, footerRef);

    return () => ctx.revert();
  }, [reduceMotion]);

  const handleInputChange = useCallback(
    (event) => {
      const { name, value } = event.target;
      setFormData((prev) => ({ ...prev, [name]: value }));
      if (errors[name]) {
        setErrors((prev) => ({ ...prev, [name]: '' }));
      }
    },
    [errors],
  );

  const validateForm = useCallback(() => {
    const nextErrors = {};
    if (!formData.name.trim()) nextErrors.name = t('footer.errors.name');
    if (!formData.email.trim()) {
      nextErrors.email = t('footer.errors.emailRequired');
    } else if (!EMAIL_REGEX.test(formData.email)) {
      nextErrors.email = t('footer.errors.emailInvalid');
    }
    if (!formData.phone.trim()) nextErrors.phone = t('footer.errors.phone');
    if (!formData.message.trim()) nextErrors.message = t('footer.errors.message');
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  }, [formData, t]);

  const buildWhatsappMessage = useCallback(
    () =>
      [
        t('footer.whatsapp.greeting'),
        '',
        t('footer.whatsapp.intro'),
        '',
        `*${t('footer.whatsapp.dataHeader')}*`,
        `📝 *${t('footer.whatsapp.name')}:* ${formData.name}`,
        `📧 *${t('footer.whatsapp.email')}:* ${formData.email}`,
        `📱 *${t('footer.whatsapp.phone')}:* ${formData.phone}`,
        '',
        `💬 *${t('footer.whatsapp.messageHeader')}:*`,
        formData.message,
        '',
        `_${t('footer.whatsapp.signature')}_`,
      ].join('\n'),
    [formData, t],
  );

  const handleSubmit = useCallback(
    (event) => {
      event.preventDefault();

      if (!validateForm()) {
        if (!reduceMotion && formRef.current) {
          gsap.fromTo(
            formRef.current,
            { x: -10 },
            {
              x: 10,
              duration: 0.1,
              repeat: 3,
              yoyo: true,
              onComplete: () => gsap.set(formRef.current, { x: 0 }),
            },
          );
        }
        return;
      }

      const url = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(
        buildWhatsappMessage(),
      )}`;
      window.open(url, '_blank', 'noopener,noreferrer');
      setFormData(EMPTY_FORM);
    },
    [buildWhatsappMessage, reduceMotion, validateForm],
  );

  return (
    <footer
      className="footer"
      id="contacto"
      ref={footerRef}
      aria-labelledby="footer-title"
    >
      <div className="container">
        <div className="footer__content">
          <div className="footer__left">
            <h2 id="footer-title" className="footer__title">
              {t('footer.title')}
            </h2>
            <h3 className="footer__subtitle">{t('footer.subtitle')}</h3>

            <form
              className="footer__form"
              onSubmit={handleSubmit}
              ref={formRef}
              noValidate
            >
              <div className="footer__form-group">
                <label htmlFor="footer-name" className="visually-hidden">
                  {t('footer.form.name')}
                </label>
                <input
                  id="footer-name"
                  type="text"
                  name="name"
                  autoComplete="name"
                  placeholder={t('footer.form.name')}
                  className={`footer__input ${errors.name ? 'footer__input--error' : ''}`}
                  value={formData.name}
                  onChange={handleInputChange}
                  aria-invalid={Boolean(errors.name)}
                  aria-describedby={errors.name ? 'footer-name-error' : undefined}
                />
                {errors.name && (
                  <span id="footer-name-error" className="footer__error" role="alert">
                    {errors.name}
                  </span>
                )}
              </div>

              <div className="footer__form-group">
                <label htmlFor="footer-email" className="visually-hidden">
                  {t('footer.form.email')}
                </label>
                <input
                  id="footer-email"
                  type="email"
                  name="email"
                  autoComplete="email"
                  placeholder={t('footer.form.email')}
                  className={`footer__input ${errors.email ? 'footer__input--error' : ''}`}
                  value={formData.email}
                  onChange={handleInputChange}
                  aria-invalid={Boolean(errors.email)}
                  aria-describedby={errors.email ? 'footer-email-error' : undefined}
                />
                {errors.email && (
                  <span id="footer-email-error" className="footer__error" role="alert">
                    {errors.email}
                  </span>
                )}
              </div>

              <div className="footer__form-group">
                <label htmlFor="footer-phone" className="visually-hidden">
                  {t('footer.form.phone')}
                </label>
                <input
                  id="footer-phone"
                  type="tel"
                  name="phone"
                  autoComplete="tel"
                  placeholder={t('footer.form.phone')}
                  className={`footer__input ${errors.phone ? 'footer__input--error' : ''}`}
                  value={formData.phone}
                  onChange={handleInputChange}
                  aria-invalid={Boolean(errors.phone)}
                  aria-describedby={errors.phone ? 'footer-phone-error' : undefined}
                />
                {errors.phone && (
                  <span id="footer-phone-error" className="footer__error" role="alert">
                    {errors.phone}
                  </span>
                )}
              </div>

              <div className="footer__form-group">
                <label htmlFor="footer-message" className="visually-hidden">
                  {t('footer.form.message')}
                </label>
                <textarea
                  id="footer-message"
                  name="message"
                  placeholder={t('footer.form.message')}
                  className={`footer__textarea ${errors.message ? 'footer__input--error' : ''}`}
                  rows="5"
                  value={formData.message}
                  onChange={handleInputChange}
                  aria-invalid={Boolean(errors.message)}
                  aria-describedby={errors.message ? 'footer-message-error' : undefined}
                />
                {errors.message && (
                  <span id="footer-message-error" className="footer__error" role="alert">
                    {errors.message}
                  </span>
                )}
              </div>

              <button type="submit" className="footer__submit">
                <span>{t('footer.form.submit')}</span>
                <svg
                  className="footer__submit-icon"
                  viewBox="0 0 24 24"
                  width="20"
                  height="20"
                  aria-hidden="true"
                  focusable="false"
                >
                  <path
                    fill="currentColor"
                    d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"
                  />
                </svg>
              </button>
            </form>
          </div>

          <div className="footer__right">
            <div className="footer__map-container">
              <div className="footer__map">
                <iframe
                  src={MAP_EMBED_URL}
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="H Group — ubicación en Google Maps"
                />
              </div>
            </div>

            <div className="footer__info">
              <div className="footer__logo">
                <img src={hgroupNegro} alt="H Group" loading="lazy" decoding="async" />
              </div>
              <address className="footer__address-wrapper">
                {GOOGLE_MAPS_URL ? (
                  <a
                    className="footer__address"
                    href={GOOGLE_MAPS_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={t('footer.addressAria')}
                  >
                    Blvd. Palmas Hills 1, Piso 21<br />
                    Villa de las Palmas, 52787 Naucalpan<br />
                    de Juárez, Méx.
                  </a>
                ) : (
                  <span className="footer__address footer__address--static">
                    Blvd. Palmas Hills 1, Piso 21<br />
                    Villa de las Palmas, 52787 Naucalpan<br />
                    de Juárez, Méx.
                  </span>
                )}
              </address>
              <div className="footer__social">
                <a
                  href="https://www.instagram.com/hgroupp_/"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Instagram"
                >
                  <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zM5.838 12a6.162 6.162 0 1112.324 0 6.162 6.162 0 01-12.324 0zM12 16a4 4 0 110-8 4 4 0 010 8zm4.965-10.405a1.44 1.44 0 112.881.001 1.44 1.44 0 01-2.881-.001z" />
                  </svg>
                </a>
                <a
                  href="https://www.linkedin.com/company/herohgroup/posts/?feedView=all"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="LinkedIn"
                >
                  <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                  </svg>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
